import { CheckCircle2, ChevronRight, XCircle } from 'lucide-react';
import React from 'react';
import { UseCaseSummary } from '../../types';

import { useTranslation } from 'react-i18next';
import { getUseCaseStatusLabel } from './useCaseStatus';

interface UseCaseListTableProps {
  useCases: UseCaseSummary[];
  onViewDetailsClick: (useCaseId: string) => void; // Callback pour la navigation
  // onEditUseCase est supprimé
}

const UseCaseListTable: React.FC<UseCaseListTableProps> = ({ useCases, onViewDetailsClick }) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <table className="w-full text-sm text-left text-muted-foreground">
        <thead className="text-xs uppercase bg-muted/50 text-muted-foreground">
          <tr>
            <th scope="col" className="px-6 py-3">
              {t('useCases.table.columns.useCase')}
            </th>
            <th scope="col" className="px-6 py-3">
              {t('useCases.table.columns.valueScore')}
            </th>
            <th scope="col" className="px-6 py-3 text-center">
              {t('useCases.table.columns.complexityScore')}
            </th>
            <th scope="col" className="px-6 py-3">
              {t('useCases.table.columns.status')}
            </th>
            <th scope="col" className="px-6 py-3 text-center">
              {t('useCases.table.columns.cadrage', 'Solution')}
            </th>
            <th scope="col" className="px-6 py-3 text-center">
              {t('useCases.table.columns.forgeApp', 'Forge app')}
            </th>
            {/* Colonne Actions supprimée, remplacée par icône */}
            <th scope="col" className="px-1 py-3 text-center"> {/* Réduire padding */}
              {/* Vide ou une icône générique si besoin */}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {useCases && useCases.length > 0 ? (
            useCases.map((useCase) => (
              (() => {
                const hasCadrage = useCase.has_linked_cadrage ?? Boolean(useCase.cadrage_id);
                const hasForgeApp = Boolean(useCase.forge_application_id);
                return (
              <tr
                key={useCase.id}
                className="bg-card hover:bg-muted/30 cursor-pointer"
                onClick={() => onViewDetailsClick(useCase.id)} // Rendre la ligne cliquable
              >
                <th scope="row" className="px-6 py-4 font-medium text-foreground whitespace-nowrap">
                  {useCase.name}
                </th>
                <td className="px-6 py-4 text-center text-foreground">
                  {useCase.value_score ?? '-'} {/* Correction: camelCase -> snake_case */}
                </td>
                <td className="px-6 py-4 text-center text-foreground">
                  {useCase.complexity_score ?? '-'} {/* Correction: camelCase -> snake_case */}
                </td>
                <td className="px-6 py-4 text-foreground/80">
                  {getUseCaseStatusLabel(useCase.status, t)}
                </td>
                <td className="px-6 py-4 text-center">
                  {hasCadrage ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      {t('global.yes', 'Yes')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      <XCircle className="h-3.5 w-3.5 shrink-0" />
                      {t('global.no', 'No')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-center">
                  {hasCadrage ? (
                    hasForgeApp ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                        {t('global.yes', 'Yes')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        <XCircle className="h-3.5 w-3.5 shrink-0" />
                        {t('global.no', 'No')}
                      </span>
                    )
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                {/* Cellule avec icône flèche */}
                <td className="px-1 py-4 text-center text-muted-foreground"> {/* Réduire padding */}
                  <ChevronRight className="h-5 w-5 inline-block" />
                </td>
              </tr>
                );
              })()
            ))
          ) : (
            <tr>
              {/* Ajuster colSpan car une colonne a été modifiée */}
              <td colSpan={7} className="px-6 py-4 text-center text-muted-foreground">
                {t('useCases.table.empty')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UseCaseListTable;
